import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useLanguageStore } from '@/stores/useLanguageStore';
import type { MVPLanguageCode } from '@/types/language';
import { isMVPLanguageCode, DEFAULT_LANGUAGE_CODE } from '@/types/language';

/**
 * Learning Store
 *
 * Manages learning activities, progress, and performance metrics.
 *
 * DEPRECATION NOTICE: `currentLanguage` and `setCurrentLanguage` are deprecated.
 * They are maintained for backwards compatibility but delegate to `useLanguageStore`.
 *
 * For new code, use:
 *   import { useLanguageStore } from '@/stores/useLanguageStore';
 *   const { targetLanguage, setTargetLanguage } = useLanguageStore();
 *
 * @see /docs/plans/multi-language-support-implementation-plan.md
 */

export interface LearningActivity {
  id: string;
  type: 'conversation' | 'lesson' | 'srs_review';
  title: string;
  description?: string;
  progress: number; // 0-100
  estimatedTime: number; // minutes
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  lastAccessed: string;
  completed: boolean;
}

export interface ProgressData {
  totalWordsKnown: number;
  totalConversations: number;
  totalLessonsCompleted: number;
  currentStreak: number;
  longestStreak: number;
  weeklyGoal: number;
  weeklyProgress: number;
  cefr_level: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
}

export interface AIRecommendation {
  id: string;
  type: 'conversation' | 'lesson' | 'review';
  title: string;
  description: string;
  reasoning: string;
  priority: 'high' | 'medium' | 'low';
  estimatedTime: number;
  created_at: string;
}

export interface LearningState {
  /**
   * @deprecated Use `useLanguageStore().targetLanguage` instead.
   * This property is maintained for backwards compatibility and delegates to useLanguageStore.
   */
  currentLanguage: string;

  lastActivity: LearningActivity | null;
  progress: ProgressData | null;
  recommendations: AIRecommendation[];
  isLoading: boolean;
  error: string | null;
  
  // Real-time sync status
  syncStatus: 'idle' | 'syncing' | 'error' | 'offline';
  lastSyncTime: string | null;
  
  // Spanish vocabulary integration
  spanishProgress: {
    totalVocabularyLearned: number;
    masteredWordsByLevel: Record<string, number>;
    topicCategoriesProgress: Record<string, number>;
    regionalVariantsLearned: string[];
    averageRetentionRate: number;
    vocabularyMilestones: any[];
  } | null;
  
  // Performance metrics
  performanceMetrics: {
    averageSessionTime: number;
    wordsPerMinute: number;
    accuracyRate: number;
    streakConsistency: number;
    engagementScore: number;
  } | null;
  
  // Actions
  /**
   * @deprecated Use `useLanguageStore().setTargetLanguage()` instead.
   * This method is maintained for backwards compatibility and delegates to useLanguageStore.
   */
  setCurrentLanguage: (language: string) => void;
  setLastActivity: (activity: LearningActivity | null) => void;
  setProgress: (progress: ProgressData) => void;
  updateProgress: (updates: Partial<ProgressData>) => void;
  setRecommendations: (recommendations: AIRecommendation[]) => void;
  addRecommendation: (recommendation: AIRecommendation) => void;
  removeRecommendation: (id: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSyncStatus: (status: LearningState['syncStatus']) => void;
  markSynced: () => void;
  
  // Activity tracking
  startActivity: (activity: LearningActivity) => void;
  completeActivity: (activityId: string, progress?: number) => void;
  updateActivityProgress: (activityId: string, progress: number) => void;
  
  // Spanish vocabulary integration
  setSpanishProgress: (progress: LearningState['spanishProgress']) => void;
  updateSpanishProgress: (updates: Partial<NonNullable<LearningState['spanishProgress']>>) => void;
  incrementWordsKnown: (count?: number) => void;
  updateTopicCategoryProgress: (category: string, progress: number) => void;
  addRegionalVariant: (variant: string) => void;
  
  // Performance tracking
  setPerformanceMetrics: (metrics: LearningState['performanceMetrics']) => void;
  updatePerformanceMetrics: (updates: Partial<NonNullable<LearningState['performanceMetrics']>>) => void;
  trackSessionPerformance: (sessionTime: number, wordsCount: number, accuracy: number) => void;
}

export const useLearningStore = create<LearningState>()(
  persist(
    (set, get) => ({
      // DEPRECATED: currentLanguage delegates to useLanguageStore
      // Initialized with default, then synced via subscription after stores are ready
      currentLanguage: 'es', // Default - will be synced from useLanguageStore
      lastActivity: null,
      progress: null,
      recommendations: [],
      isLoading: false,
      error: null,
      syncStatus: 'idle',
      lastSyncTime: null,
      
      // Spanish vocabulary integration
      spanishProgress: {
        totalVocabularyLearned: 0,
        masteredWordsByLevel: {
          'A1': 0,
          'A2': 0,
          'B1': 0,
          'B2': 0,
          'C1': 0,
          'C2': 0
        },
        topicCategoriesProgress: {},
        regionalVariantsLearned: [],
        averageRetentionRate: 0,
        vocabularyMilestones: []
      },
      
      // Performance metrics
      performanceMetrics: {
        averageSessionTime: 0,
        wordsPerMinute: 0,
        accuracyRate: 0,
        streakConsistency: 0,
        engagementScore: 0
      },

      /**
       * @deprecated Use useLanguageStore().setTargetLanguage() instead
       * This delegates to useLanguageStore and updates local state for compatibility
       */
      setCurrentLanguage: (language: string) => {
        // Validate and delegate to useLanguageStore
        const validLanguage = isMVPLanguageCode(language) ? language : DEFAULT_LANGUAGE_CODE;
        useLanguageStore.getState().setTargetLanguage(validLanguage);
        // Also update local state for immediate reactivity
        set({ currentLanguage: validLanguage });
      },

      setLastActivity: (activity) => {
        set({ lastActivity: activity });
      },

      setProgress: (progress) => {
        set({ progress });
      },

      updateProgress: (updates) => {
        const currentProgress = get().progress;
        if (currentProgress) {
          set({
            progress: { ...currentProgress, ...updates }
          });
        }
      },

      setRecommendations: (recommendations) => {
        set({ recommendations });
      },

      addRecommendation: (recommendation) => {
        const current = get().recommendations;
        set({
          recommendations: [recommendation, ...current].slice(0, 5) // Keep only 5 latest
        });
      },

      removeRecommendation: (id) => {
        const current = get().recommendations;
        set({
          recommendations: current.filter(r => r.id !== id)
        });
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setError: (error) => {
        set({ error, isLoading: false });
      },

      setSyncStatus: (syncStatus) => {
        set({ syncStatus });
      },

      markSynced: () => {
        set({ 
          syncStatus: 'idle',
          lastSyncTime: new Date().toISOString(),
          error: null
        });
      },

      startActivity: (activity) => {
        set({ 
          lastActivity: { 
            ...activity, 
            lastAccessed: new Date().toISOString() 
          }
        });
      },

      completeActivity: (activityId, progressValue = 100) => {
        const { lastActivity, progress } = get();
        
        if (lastActivity?.id === activityId) {
          const completedActivity = {
            ...lastActivity,
            progress: progressValue,
            completed: progressValue >= 100,
            lastAccessed: new Date().toISOString()
          };
          
          set({ lastActivity: completedActivity });
          
          // Update overall progress
          if (progress && progressValue >= 100) {
            const updates: Partial<ProgressData> = {};
            
            if (completedActivity.type === 'conversation') {
              updates.totalConversations = progress.totalConversations + 1;
            } else if (completedActivity.type === 'lesson') {
              updates.totalLessonsCompleted = progress.totalLessonsCompleted + 1;
            }
            
            if (Object.keys(updates).length > 0) {
              get().updateProgress(updates);
            }
          }
        }
      },

      updateActivityProgress: (activityId, progressValue) => {
        const { lastActivity } = get();
        
        if (lastActivity?.id === activityId) {
          set({
            lastActivity: {
              ...lastActivity,
              progress: progressValue,
              lastAccessed: new Date().toISOString()
            }
          });
        }
      },
      
      // Spanish vocabulary integration actions
      setSpanishProgress: (spanishProgress) => {
        set({ spanishProgress });
      },
      
      updateSpanishProgress: (updates) => {
        const current = get().spanishProgress;
        if (current) {
          set({
            spanishProgress: { ...current, ...updates }
          });
        }
      },
      
      incrementWordsKnown: (count = 1) => {
        const current = get().spanishProgress;
        if (current) {
          set({
            spanishProgress: {
              ...current,
              totalVocabularyLearned: current.totalVocabularyLearned + count
            }
          });
        }
        
        // Also update main progress
        const mainProgress = get().progress;
        if (mainProgress) {
          get().updateProgress({
            totalWordsKnown: mainProgress.totalWordsKnown + count
          });
        }
      },
      
      updateTopicCategoryProgress: (category, progress) => {
        const current = get().spanishProgress;
        if (current) {
          set({
            spanishProgress: {
              ...current,
              topicCategoriesProgress: {
                ...current.topicCategoriesProgress,
                [category]: progress
              }
            }
          });
        }
      },
      
      addRegionalVariant: (variant) => {
        const current = get().spanishProgress;
        if (current && !current.regionalVariantsLearned.includes(variant)) {
          set({
            spanishProgress: {
              ...current,
              regionalVariantsLearned: [...current.regionalVariantsLearned, variant]
            }
          });
        }
      },
      
      // Performance tracking actions
      setPerformanceMetrics: (performanceMetrics) => {
        set({ performanceMetrics });
      },
      
      updatePerformanceMetrics: (updates) => {
        const current = get().performanceMetrics;
        if (current) {
          set({
            performanceMetrics: { ...current, ...updates }
          });
        }
      },
      
      trackSessionPerformance: (sessionTime, wordsCount, accuracy) => {
        const current = get().performanceMetrics;
        if (current) {
          // Calculate words per minute
          const wordsPerMinute = sessionTime > 0 ? (wordsCount / (sessionTime / 60000)) : 0;
          
          // Update metrics with weighted averages
          const sessionWeight = 0.2; // 20% weight for new session
          
          set({
            performanceMetrics: {
              ...current,
              averageSessionTime: current.averageSessionTime * (1 - sessionWeight) + sessionTime * sessionWeight,
              wordsPerMinute: current.wordsPerMinute * (1 - sessionWeight) + wordsPerMinute * sessionWeight,
              accuracyRate: current.accuracyRate * (1 - sessionWeight) + accuracy * sessionWeight,
              engagementScore: Math.min(100, current.engagementScore + (accuracy > 0.8 ? 2 : -1))
            }
          });
        }
      }
    }),
    {
      name: 'converlang-learning',
      storage: createJSONStorage(() => localStorage),
      // Exclude temporary states from persistence
      // NOTE: currentLanguage is now persisted in useLanguageStore
      partialize: (state) => ({
        lastActivity: state.lastActivity,
        progress: state.progress,
        recommendations: state.recommendations,
        lastSyncTime: state.lastSyncTime,
        spanishProgress: state.spanishProgress,
        performanceMetrics: state.performanceMetrics
      })
    }
  )
);

// ============================================================================
// Language Store Synchronization
// ============================================================================
// Subscribe to useLanguageStore to keep currentLanguage in sync
// This provides backwards compatibility for code using useLearningStore.currentLanguage

// Use setTimeout to defer initialization until after all stores are loaded
// This avoids circular dependency issues at module initialization time
setTimeout(() => {
  // Initial sync - get current value from language store
  const initialLanguage = useLanguageStore.getState().targetLanguage;
  if (useLearningStore.getState().currentLanguage !== initialLanguage) {
    useLearningStore.setState({ currentLanguage: initialLanguage });
  }

  // Subscribe to future changes
  useLanguageStore.subscribe((state) => {
    const currentLearningLanguage = useLearningStore.getState().currentLanguage;
    if (currentLearningLanguage !== state.targetLanguage) {
      useLearningStore.setState({ currentLanguage: state.targetLanguage });
    }
  });
}, 0);